import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Edit, Plus, Music, Disc3, User } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";

interface Artist {
  id: string;
  name: string;
  description: string | null;
  image_url: string | null;
  published: boolean;
}

interface Album {
  id: string;
  artist_id: string | null;
  title: string;
  description: string | null;
  cover_image_url: string | null;
  published: boolean;
}

interface Disk {
  id: string;
  album_id: string;
  disk_number: number;
  disk_name: string | null;
}

interface Song {
  id: string;
  disk_id: string;
  title: string;
  duration: string | null;
  audio_file_path: string;
  lyrics: string | null;
  chord_sheet_path: string | null;
  display_order: number;
  published: boolean;
  song_number: number | null;
  description: string | null;
}

export const MusicManagement = () => {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const { isAdmin } = useAuth();
  
  // State for each level
  const [artists, setArtists] = useState<Artist[]>([]);
  const [albums, setAlbums] = useState<Album[]>([]);
  const [disks, setDisks] = useState<Disk[]>([]);
  const [songs, setSongs] = useState<Song[]>([]);
  
  // Selection state
  const [selectedArtist, setSelectedArtist] = useState<string | null>(null);
  const [selectedAlbum, setSelectedAlbum] = useState<string | null>(null);
  const [selectedDisk, setSelectedDisk] = useState<string | null>(null);
  
  // Dialog state
  const [isArtistDialogOpen, setIsArtistDialogOpen] = useState(false);
  const [isAlbumDialogOpen, setIsAlbumDialogOpen] = useState(false);
  const [isDiskDialogOpen, setIsDiskDialogOpen] = useState(false);
  const [isSongDialogOpen, setIsSongDialogOpen] = useState(false);
  
  // Editing state
  const [editingArtist, setEditingArtist] = useState<Artist | null>(null);
  const [editingAlbum, setEditingAlbum] = useState<Album | null>(null);
  const [editingDisk, setEditingDisk] = useState<Disk | null>(null);
  const [editingSong, setEditingSong] = useState<Song | null>(null);
  
  // Loading state
  const [uploading, setUploading] = useState(false);
  const [translating, setTranslating] = useState(false);

  // Form state
  const [artistForm, setArtistForm] = useState({
    name: "",
    description: "",
    image_url: "",
    published: false,
  });

  const [albumForm, setAlbumForm] = useState({
    title: "",
    description: "",
    cover_image_url: "",
    published: false,
  });

  const [diskForm, setDiskForm] = useState({
    disk_number: "",
    disk_name: "",
  });

  const [songForm, setSongForm] = useState({
    title: "",
    duration: "",
    lyrics: "",
    description: "",
    song_number: "",
    display_order: 0,
    published: false,
    apple_music_url: "",
  });

  const [uploadProgress, setUploadProgress] = useState(0);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [chordSheetFile, setChordSheetFile] = useState<File | null>(null);
  const [isDraggingAudio, setIsDraggingAudio] = useState(false);

  // Fetch artists on mount
  useEffect(() => {
    fetchArtists();
  }, [language]);

  // Fetch albums when artist is selected
  useEffect(() => {
    if (selectedArtist) {
      fetchAlbums(selectedArtist);
    } else {
      setAlbums([]);
      setSelectedAlbum(null);
      setDisks([]);
      setSelectedDisk(null);
      setSongs([]);
    }
  }, [selectedArtist, language]);

  // Fetch disks when album is selected
  useEffect(() => {
    if (selectedAlbum) {
      fetchDisks(selectedAlbum);
    } else {
      setDisks([]);
      setSelectedDisk(null);
      setSongs([]);
    }
  }, [selectedAlbum, language]);

  // Fetch songs when disk is selected
  useEffect(() => {
    if (selectedDisk) {
      fetchSongs(selectedDisk);
    } else {
      setSongs([]);
    }
  }, [selectedDisk, language]);

  const fetchArtists = async () => {
    const { data: artistsData } = await supabase
      .from("artists")
      .select("*")
      .order("name");

    let translatedArtists = artistsData || [];
    if (artistsData && artistsData.length > 0) {
      const { data: artistTransData } = await supabase
        .from("artists_translations")
        .select("*")
        .in("artist_id", artistsData.map(a => a.id))
        .eq("language", language);
      
      translatedArtists = artistsData.map(artist => {
        const trans = artistTransData?.find(t => t.artist_id === artist.id);
        return {
          ...artist,
          name: trans?.name || artist.name,
          description: trans?.description || artist.description,
        };
      });
    }

    setArtists(translatedArtists);
  };

  const fetchAlbums = async (artistId: string) => {
    const { data: albumsData } = await supabase
      .from("albums")
      .select("*")
      .eq("artist_id", artistId)
      .order("title");

    let translatedAlbums = albumsData || [];
    if (albumsData && albumsData.length > 0) {
      const { data: albumTransData } = await supabase
        .from("albums_translations")
        .select("*")
        .in("album_id", albumsData.map(a => a.id))
        .eq("language", language);
      
      translatedAlbums = albumsData.map(album => {
        const trans = albumTransData?.find(t => t.album_id === album.id);
        return {
          ...album,
          title: trans?.title || album.title,
          description: trans?.description || album.description,
        };
      });
    }

    setAlbums(translatedAlbums);
    if (translatedAlbums.length > 0 && !selectedAlbum) {
      setSelectedAlbum(translatedAlbums[0].id);
    }
  };

  const fetchDisks = async (albumId: string) => {
    const { data: disksData } = await supabase
      .from("disks")
      .select("*")
      .eq("album_id", albumId)
      .order("disk_number");

    let translatedDisks = disksData || [];
    if (disksData && disksData.length > 0) {
      const { data: diskTransData } = await supabase
        .from("disks_translations")
        .select("*")
        .in("disk_id", disksData.map(d => d.id))
        .eq("language", language);
      
      translatedDisks = disksData.map(disk => {
        const trans = diskTransData?.find(t => t.disk_id === disk.id);
        return {
          ...disk,
          disk_name: trans?.disk_name || disk.disk_name,
        };
      });
    }

    setDisks(translatedDisks);
    if (translatedDisks.length > 0 && !selectedDisk) {
      setSelectedDisk(translatedDisks[0].id);
    }
  };

  const fetchSongs = async (diskId: string) => {
    const { data: songsData } = await supabase
      .from("songs")
      .select("*")
      .eq("disk_id", diskId)
      .order("song_number", { nullsFirst: false })
      .order("display_order");

    let translatedSongs = songsData || [];
    if (songsData && songsData.length > 0) {
      const { data: songTransData } = await supabase
        .from("songs_translations")
        .select("*")
        .in("song_id", songsData.map(s => s.id))
        .eq("language", language);
      
      translatedSongs = songsData.map(song => {
        const trans = songTransData?.find(t => t.song_id === song.id);
        return {
          ...song,
          title: trans?.title || song.title,
          lyrics: trans?.lyrics || song.lyrics,
          description: trans?.description || song.description,
        };
      });
    }

    setSongs(translatedSongs);
  };

  const handleArtistSubmit = async () => {
    try {
      setTranslating(true);
      let artistId: string;

      if (editingArtist) {
        const { error } = await supabase
          .from("artists")
          .update(artistForm)
          .eq("id", editingArtist.id);

        if (error) throw error;
        artistId = editingArtist.id;
      } else {
        const { data, error } = await supabase
          .from("artists")
          .insert([artistForm])
          .select()
          .single();

        if (error) throw error;
        artistId = data.id;
      }

      // Auto-translate to other languages
      const otherLanguages = ['en', 'ru', 'es'].filter(lang => lang !== language);
      const contentToTranslate: any = { name: artistForm.name };
      if (artistForm.description) contentToTranslate.description = artistForm.description;

      const { data: translationData, error: translationError } = await supabase.functions.invoke(
        'translate-content',
        {
          body: {
            content: contentToTranslate,
            fromLanguage: language,
            toLanguages: otherLanguages,
          },
        }
      );

      if (!translationError && translationData?.translations) {
        for (const [lang, trans] of Object.entries(translationData.translations)) {
          await supabase.from("artists_translations").upsert(
            {
              artist_id: artistId,
              language: lang,
              name: (trans as any).name || artistForm.name,
              description: (trans as any).description || artistForm.description,
            },
            { onConflict: 'artist_id,language' }
          );
        }
      }

      // Save source language
      await supabase.from("artists_translations").upsert(
        {
          artist_id: artistId,
          language: language,
          name: artistForm.name,
          description: artistForm.description || null,
        },
        { onConflict: 'artist_id,language' }
      );

      toast({ title: t("admin.music.success"), description: "Artist saved successfully" });
      setIsArtistDialogOpen(false);
      resetArtistForm();
      fetchArtists();
    } catch (error: any) {
      toast({
        title: t("music.error"),
        description: t("admin.music.errorSaving"),
        variant: "destructive",
      });
    } finally {
      setTranslating(false);
    }
  };

  const handleAlbumSubmit = async () => {
    if (!selectedArtist) return;
    
    try {
      setTranslating(true);
      let albumId: string;

      const albumData = {
        ...albumForm,
        artist_id: selectedArtist,
      };

      if (editingAlbum) {
        const { error } = await supabase
          .from("albums")
          .update(albumData)
          .eq("id", editingAlbum.id);

        if (error) throw error;
        albumId = editingAlbum.id;
      } else {
        const { data, error } = await supabase
          .from("albums")
          .insert([albumData])
          .select()
          .single();

        if (error) throw error;
        albumId = data.id;
      }

      // Auto-translate to other languages
      const otherLanguages = ['en', 'ru', 'es'].filter(lang => lang !== language);
      const contentToTranslate: any = { title: albumForm.title };
      if (albumForm.description) contentToTranslate.description = albumForm.description;

      const { data: translationData, error: translationError } = await supabase.functions.invoke(
        'translate-content',
        {
          body: {
            content: contentToTranslate,
            fromLanguage: language,
            toLanguages: otherLanguages,
          },
        }
      );

      if (!translationError && translationData?.translations) {
        for (const [lang, trans] of Object.entries(translationData.translations)) {
          await supabase.from("albums_translations").upsert(
            {
              album_id: albumId,
              language: lang,
              title: (trans as any).title || albumForm.title,
              description: (trans as any).description || albumForm.description,
            },
            { onConflict: 'album_id,language' }
          );
        }
      }

      // Save source language
      await supabase.from("albums_translations").upsert(
        {
          album_id: albumId,
          language: language,
          title: albumForm.title,
          description: albumForm.description || null,
        },
        { onConflict: 'album_id,language' }
      );

      toast({ title: t("admin.music.success"), description: t("admin.music.albumSaved") });
      setIsAlbumDialogOpen(false);
      resetAlbumForm();
      if (selectedArtist) fetchAlbums(selectedArtist);
    } catch (error: any) {
      toast({
        title: t("music.error"),
        description: t("admin.music.errorSaving"),
        variant: "destructive",
      });
    } finally {
      setTranslating(false);
    }
  };

  const handleDiskSubmit = async () => {
    if (!selectedAlbum) return;
    
    try {
      setTranslating(true);
      const diskNumber = parseInt(diskForm.disk_number);
      
      if (isNaN(diskNumber)) {
        toast({
          title: t("music.error"),
          description: "Disk number is required",
          variant: "destructive",
        });
        return;
      }

      let diskId: string;

      if (editingDisk) {
        const { error } = await supabase
          .from("disks")
          .update({
            disk_number: diskNumber,
            disk_name: diskForm.disk_name || null,
          })
          .eq("id", editingDisk.id);

        if (error) throw error;
        diskId = editingDisk.id;
      } else {
        const { data, error } = await supabase
          .from("disks")
          .insert([{
            album_id: selectedAlbum,
            disk_number: diskNumber,
            disk_name: diskForm.disk_name || null,
          }])
          .select()
          .single();

        if (error) throw error;
        diskId = data.id;
      }

      // Auto-translate disk name
      const otherLanguages = ['en', 'ru', 'es'].filter(lang => lang !== language);
      const contentToTranslate: any = {};
      if (diskForm.disk_name) contentToTranslate.disk_name = diskForm.disk_name;

      if (Object.keys(contentToTranslate).length > 0) {
        const { data: translationData } = await supabase.functions.invoke(
          'translate-content',
          {
            body: {
              content: contentToTranslate,
              fromLanguage: language,
              toLanguages: otherLanguages,
            },
          }
        );

        if (translationData?.translations) {
          for (const [lang, trans] of Object.entries(translationData.translations)) {
            await supabase.from("disks_translations").upsert(
              {
                disk_id: diskId,
                language: lang,
                disk_name: (trans as any).disk_name || diskForm.disk_name,
              },
              { onConflict: 'disk_id,language' }
            );
          }
        }

        // Save source language
        await supabase.from("disks_translations").upsert(
          {
            disk_id: diskId,
            language: language,
            disk_name: diskForm.disk_name || null,
          },
          { onConflict: 'disk_id,language' }
        );
      }

      toast({ title: t("admin.music.success"), description: "Disk saved successfully" });
      setIsDiskDialogOpen(false);
      resetDiskForm();
      if (selectedAlbum) fetchDisks(selectedAlbum);
    } catch (error: any) {
      toast({
        title: t("music.error"),
        description: error.message || "Error saving disk",
        variant: "destructive",
      });
    } finally {
      setTranslating(false);
    }
  };

  const handleSongSubmit = async () => {
    if (!selectedDisk) return;

    try {
      setUploading(true);
      setTranslating(true);
      setUploadProgress(10);

      let audioPath = editingSong?.audio_file_path || "";
      let chordPath = editingSong?.chord_sheet_path || null;

      // Validate that either audio file or Apple Music URL is provided
      if (!audioFile && !songForm.apple_music_url && !editingSong) {
        toast({
          title: t("admin.music.error"),
          description: "Please provide either an audio file or an Apple Music link",
          variant: "destructive",
        });
        setUploading(false);
        setTranslating(false);
        setUploadProgress(0);
        return;
      }

      if (audioFile) {
        setUploadProgress(30);
        const fileName = `${Date.now()}-${audioFile.name}`;
        const { error: uploadError } = await supabase.storage
          .from("music-files")
          .upload(fileName, audioFile);

        if (uploadError) throw uploadError;
        audioPath = fileName;
        setUploadProgress(50);
      }

      if (chordSheetFile) {
        setUploadProgress(60);
        const fileName = `chords-${Date.now()}-${chordSheetFile.name}`;
        const { error: uploadError } = await supabase.storage
          .from("music-files")
          .upload(fileName, chordSheetFile);

        if (uploadError) throw uploadError;
        chordPath = fileName;
        setUploadProgress(70);
      }

      setUploadProgress(75);
      
      const songData = {
        disk_id: selectedDisk,
        audio_file_path: audioPath,
        chord_sheet_path: chordPath,
        song_number: songForm.song_number ? parseInt(songForm.song_number) : null,
        display_order: songForm.display_order,
        published: songForm.published,
        duration: songForm.duration,
        title: songForm.title,
        lyrics: songForm.lyrics,
        description: songForm.description,
        apple_music_url: songForm.apple_music_url || null,
      };

      let songId: string;

      if (editingSong) {
        const { error } = await supabase
          .from("songs")
          .update(songData)
          .eq("id", editingSong.id);

        if (error) throw error;
        songId = editingSong.id;
      } else {
        const { data, error } = await supabase.from("songs").insert([songData]).select().single();

        if (error) throw error;
        songId = data.id;
      }

      setUploadProgress(80);

      // Auto-translate to other languages
      const otherLanguages = ['en', 'ru', 'es'].filter(lang => lang !== language);
      const contentToTranslate: any = { title: songForm.title };
      if (songForm.lyrics) contentToTranslate.lyrics = songForm.lyrics;
      if (songForm.description) contentToTranslate.description = songForm.description;

      const { data: translationData } = await supabase.functions.invoke(
        'translate-content',
        {
          body: {
            content: contentToTranslate,
            fromLanguage: language,
            toLanguages: otherLanguages,
          },
        }
      );

      if (translationData?.translations) {
        for (const [lang, trans] of Object.entries(translationData.translations)) {
          await supabase.from("songs_translations").upsert(
            {
              song_id: songId,
              language: lang,
              title: (trans as any).title || songForm.title,
              lyrics: (trans as any).lyrics || songForm.lyrics,
              description: (trans as any).description || songForm.description,
            },
            { onConflict: 'song_id,language' }
          );
        }
      }

      // Save source language
      await supabase.from("songs_translations").upsert(
        {
          song_id: songId,
          language: language,
          title: songForm.title,
          lyrics: songForm.lyrics || null,
          description: songForm.description || null,
        },
        { onConflict: 'song_id,language' }
      );

      setUploadProgress(100);
      toast({ title: t("admin.music.success"), description: t("admin.music.songSaved") });

      setIsSongDialogOpen(false);
      resetSongForm();
      if (selectedDisk) fetchSongs(selectedDisk);
    } catch (error: any) {
      toast({
        title: t("music.error"),
        description: error.message || t("admin.music.errorSaving"),
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      setTranslating(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const handleDeleteArtist = async (id: string) => {
    if (!confirm("Delete this artist and all their albums, disks, and songs?")) return;

    const { error } = await supabase.from("artists").delete().eq("id", id);

    if (error) {
      toast({ title: t("music.error"), description: t("admin.music.errorDeleting"), variant: "destructive" });
    } else {
      toast({ title: t("admin.music.success"), description: "Artist deleted successfully" });
      if (selectedArtist === id) {
        setSelectedArtist(null);
      }
      fetchArtists();
    }
  };

  const handleDeleteAlbum = async (id: string) => {
    if (!confirm(t("admin.music.deleteAlbum") + "?")) return;

    const { error } = await supabase.from("albums").delete().eq("id", id);

    if (error) {
      toast({ title: t("music.error"), description: t("admin.music.errorDeleting"), variant: "destructive" });
    } else {
      toast({ title: t("admin.music.success"), description: t("admin.music.albumDeleted") });
      if (selectedAlbum === id) {
        setSelectedAlbum(null);
      }
      if (selectedArtist) fetchAlbums(selectedArtist);
    }
  };

  const handleDeleteDisk = async (id: string) => {
    if (!confirm("Delete this disk and all its songs?")) return;

    const { error } = await supabase.from("disks").delete().eq("id", id);

    if (error) {
      toast({ title: t("music.error"), description: "Error deleting disk", variant: "destructive" });
    } else {
      toast({ title: t("admin.music.success"), description: "Disk deleted successfully" });
      if (selectedDisk === id) {
        setSelectedDisk(null);
      }
      if (selectedAlbum) {
        fetchDisks(selectedAlbum);
      }
    }
  };

  const handleDeleteSong = async (id: string, audioPath: string, chordPath: string | null) => {
    if (!confirm(t("admin.music.deleteSong") + "?")) return;

    const filesToDelete = [audioPath];
    if (chordPath) filesToDelete.push(chordPath);

    await supabase.storage.from("music-files").remove(filesToDelete);

    const { error } = await supabase.from("songs").delete().eq("id", id);

    if (error) {
      toast({ title: t("music.error"), description: t("admin.music.errorDeleting"), variant: "destructive" });
    } else {
      toast({ title: t("admin.music.success"), description: t("admin.music.songDeleted") });
      if (selectedDisk) {
        fetchSongs(selectedDisk);
      }
    }
  };

  const resetArtistForm = () => {
    setArtistForm({
      name: "",
      description: "",
      image_url: "",
      published: isAdmin,
    });
    setEditingArtist(null);
  };

  const resetAlbumForm = () => {
    setAlbumForm({
      title: "",
      description: "",
      cover_image_url: "",
      published: isAdmin,
    });
    setEditingAlbum(null);
  };

  const resetDiskForm = () => {
    setDiskForm({
      disk_number: "",
      disk_name: "",
    });
    setEditingDisk(null);
  };

  const resetSongForm = () => {
    setSongForm({
      title: "",
      duration: "",
      lyrics: "",
      description: "",
      song_number: "",
      display_order: 0,
      published: isAdmin,
      apple_music_url: "",
    });
    setAudioFile(null);
    setChordSheetFile(null);
    setEditingSong(null);
    setUploadProgress(0);
  };

  const editArtist = async (artist: Artist) => {
    setEditingArtist(artist);
    
    const { data: transData } = await supabase
      .from("artists_translations")
      .select("*")
      .eq("artist_id", artist.id)
      .eq("language", language)
      .maybeSingle();
    
    setArtistForm({
      name: transData?.name || artist.name,
      description: transData?.description || artist.description || "",
      image_url: artist.image_url || "",
      published: artist.published,
    });
    setIsArtistDialogOpen(true);
  };

  const editAlbum = async (album: Album) => {
    setEditingAlbum(album);
    
    const { data: transData } = await supabase
      .from("albums_translations")
      .select("*")
      .eq("album_id", album.id)
      .eq("language", language)
      .maybeSingle();
    
    setAlbumForm({
      title: transData?.title || album.title,
      description: transData?.description || album.description || "",
      cover_image_url: album.cover_image_url || "",
      published: album.published,
    });
    setIsAlbumDialogOpen(true);
  };

  const editDisk = async (disk: Disk) => {
    setEditingDisk(disk);
    
    const { data: transData } = await supabase
      .from("disks_translations")
      .select("*")
      .eq("disk_id", disk.id)
      .eq("language", language)
      .maybeSingle();

    setDiskForm({
      disk_number: disk.disk_number.toString(),
      disk_name: transData?.disk_name || disk.disk_name || "",
    });

    setIsDiskDialogOpen(true);
  };

  const editSong = async (song: Song) => {
    setEditingSong(song);
    
    const { data: transData } = await supabase
      .from("songs_translations")
      .select("*")
      .eq("song_id", song.id)
      .eq("language", language)
      .maybeSingle();

    setSongForm({
      title: transData?.title || song.title,
      duration: song.duration || "",
      lyrics: transData?.lyrics || song.lyrics || "",
      description: transData?.description || song.description || "",
      song_number: song.song_number?.toString() || "",
      display_order: song.display_order,
      published: song.published,
      apple_music_url: (song as any).apple_music_url || "",
    });

    setIsSongDialogOpen(true);
  };

  const handleAudioDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingAudio(true);
  };

  const handleAudioDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingAudio(false);
  };

  const handleAudioDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingAudio(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === "audio/mpeg" || file.name.endsWith(".mp3")) {
        setAudioFile(file);
      } else {
        toast({ 
          title: t("music.error"), 
          description: "Please upload an MP3 file", 
          variant: "destructive" 
        });
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Artists Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              {t("admin.music.artists")}
            </CardTitle>
            <Dialog open={isArtistDialogOpen} onOpenChange={setIsArtistDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={resetArtistForm}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t("admin.music.addArtist")}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingArtist ? t("admin.music.editArtist") : t("admin.music.addArtist")}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>{t("admin.music.artistName")} *</Label>
                    <Input
                      value={artistForm.name}
                      onChange={(e) => setArtistForm({ ...artistForm, name: e.target.value })}
                      placeholder={t("admin.music.artistNamePlaceholder")}
                    />
                  </div>
                  <div>
                    <Label>{t("admin.music.description")}</Label>
                    <Textarea
                      value={artistForm.description}
                      onChange={(e) => setArtistForm({ ...artistForm, description: e.target.value })}
                      placeholder={t("admin.music.artistDescriptionPlaceholder")}
                    />
                  </div>
                  <div>
                    <Label>{t("admin.music.imageUrl")}</Label>
                    <Input
                      value={artistForm.image_url}
                      onChange={(e) => setArtistForm({ ...artistForm, image_url: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={artistForm.published}
                        onCheckedChange={(checked) =>
                          setArtistForm({ ...artistForm, published: checked })
                        }
                      />
                      <Label>{t("admin.music.published")}</Label>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button onClick={handleArtistSubmit} disabled={translating}>
                    {translating ? t("admin.music.translating") : t("admin.music.save")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {artists.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">{t("admin.music.noArtists")}</p>
            ) : (
              artists.map((artist) => (
                <div key={artist.id} className="flex items-center justify-between p-3 border rounded">
                  <div className="flex-1">
                    <p className="font-medium">{artist.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {artist.published ? t("admin.music.published") : t("admin.music.unpublished")}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="secondary"
                      onClick={() => setSelectedArtist(artist.id === selectedArtist ? null : artist.id)}
                    >
                      {artist.id === selectedArtist ? t("admin.music.hideAlbums") : t("admin.music.manageAlbums")}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => editArtist(artist)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteArtist(artist.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Albums Section */}
      {selectedArtist && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                {t("admin.music.albumsFor")} {artists.find(a => a.id === selectedArtist)?.name}
              </CardTitle>
              <Dialog open={isAlbumDialogOpen} onOpenChange={setIsAlbumDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={resetAlbumForm}>
                    <Plus className="w-4 h-4 mr-2" />
                    {t("admin.music.addAlbum")}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingAlbum ? t("admin.music.editAlbum") : t("admin.music.addAlbum")}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>{t("admin.music.albumTitle")} *</Label>
                      <Input
                        value={albumForm.title}
                        onChange={(e) => setAlbumForm({ ...albumForm, title: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>{t("admin.music.albumDescription")}</Label>
                      <Textarea
                        value={albumForm.description}
                        onChange={(e) => setAlbumForm({ ...albumForm, description: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>{t("admin.music.albumCover")}</Label>
                      <Input
                        value={albumForm.cover_image_url}
                        onChange={(e) => setAlbumForm({ ...albumForm, cover_image_url: e.target.value })}
                      />
                    </div>
                    {isAdmin && (
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={albumForm.published}
                          onCheckedChange={(checked) =>
                            setAlbumForm({ ...albumForm, published: checked })
                          }
                        />
                        <Label>{t("admin.music.published")}</Label>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button onClick={handleAlbumSubmit} disabled={translating}>
                      {translating ? t("admin.music.translating") : t("admin.music.save")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {albums.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">{t("admin.music.noAlbums")}</p>
              ) : (
                albums.map((album) => (
                  <div key={album.id} className="flex items-center justify-between p-3 border rounded">
                    <div className="flex-1">
                      <p className="font-medium">{album.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {album.published ? t("admin.music.published") : t("admin.music.unpublished")}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="secondary"
                        onClick={() => setSelectedAlbum(album.id === selectedAlbum ? null : album.id)}
                      >
                        {album.id === selectedAlbum ? t("admin.music.hideDisks") : t("admin.music.manageDisks")}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => editAlbum(album)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteAlbum(album.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Disks Section */}
      {selectedAlbum && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Disc3 className="w-5 h-5" />
              {t("admin.music.disksFor")} {albums.find(a => a.id === selectedAlbum)?.title}
            </CardTitle>
              <Dialog open={isDiskDialogOpen} onOpenChange={setIsDiskDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={resetDiskForm}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t("admin.music.addDisk")}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingDisk ? t("admin.music.editDisk") : t("admin.music.addDisk")}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>{t("admin.music.diskNumber")} *</Label>
                    <Input
                      type="number"
                      placeholder={t("admin.music.diskNumberPlaceholder")}
                      value={diskForm.disk_number}
                      onChange={(e) => setDiskForm({ ...diskForm, disk_number: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>{t("admin.music.diskName")} ({t("admin.music.optional")})</Label>
                    <Input
                      placeholder={t("admin.music.diskNamePlaceholder")}
                      value={diskForm.disk_name}
                      onChange={(e) => setDiskForm({ ...diskForm, disk_name: e.target.value })}
                    />
                  </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={handleDiskSubmit} disabled={translating}>
                      {translating ? t("admin.music.translating") : t("admin.music.save")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
          <div className="space-y-2">
            {disks.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">{t("admin.music.noDisks")}</p>
            ) : (
              disks.map((disk) => (
                <div key={disk.id} className="flex items-center justify-between p-3 border rounded">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium bg-primary/10 text-primary px-2 py-1 rounded">
                        {t("admin.music.disk")} {disk.disk_number}
                      </span>
                      {disk.disk_name && <p className="font-medium">{disk.disk_name}</p>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="secondary"
                      onClick={() => setSelectedDisk(disk.id === selectedDisk ? null : disk.id)}
                    >
                      {disk.id === selectedDisk ? t("admin.music.hideSongs") : t("admin.music.manageSongs")}
                    </Button>
                      <Button size="sm" variant="outline" onClick={() => editDisk(disk)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteDisk(disk.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Songs Section */}
      {selectedDisk && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Music className="w-5 h-5" />
              {t("admin.music.songsForDisk")} {disks.find(d => d.id === selectedDisk)?.disk_number}
            </CardTitle>
              <Dialog open={isSongDialogOpen} onOpenChange={setIsSongDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={resetSongForm}>
                    <Plus className="w-4 h-4 mr-2" />
                    {t("admin.music.addSong")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingSong ? t("admin.music.editSong") : t("admin.music.addSong")}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>{t("admin.music.songNumber")}</Label>
                      <Input
                        type="number"
                        placeholder={t("admin.music.optional")}
                        value={songForm.song_number}
                        onChange={(e) => setSongForm({ ...songForm, song_number: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>{t("admin.music.songTitle")} *</Label>
                      <Input
                        value={songForm.title}
                        onChange={(e) => setSongForm({ ...songForm, title: e.target.value })}
                        placeholder={t("admin.music.songTitle")}
                      />
                    </div>
                    <div>
                      <Label>{t("admin.music.songDescription")}</Label>
                      <Textarea
                        rows={3}
                        value={songForm.description}
                        onChange={(e) => setSongForm({ ...songForm, description: e.target.value })}
                        placeholder={t("admin.music.songDescription")}
                      />
                    </div>
                    <div>
                      <Label>{t("admin.music.lyrics")}</Label>
                      <Textarea
                        rows={6}
                        value={songForm.lyrics}
                        onChange={(e) => setSongForm({ ...songForm, lyrics: e.target.value })}
                        placeholder={t("admin.music.lyrics")}
                      />
                    </div>
                    <div>
                      <Label>{t("admin.music.duration")}</Label>
                      <Input
                        placeholder="e.g., 3:45"
                        value={songForm.duration}
                        onChange={(e) => setSongForm({ ...songForm, duration: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>{t("admin.music.appleMusicLink")}</Label>
                      <Input
                        type="url"
                        placeholder="https://music.apple.com/..."
                        value={songForm.apple_music_url}
                        onChange={(e) => setSongForm({ ...songForm, apple_music_url: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>
                        {t("admin.music.audioFile")} 
                        {!editingSong && !songForm.apple_music_url && <span className="text-destructive ml-1">*</span>}
                        {songForm.apple_music_url && <span className="text-muted-foreground text-xs ml-2">({t("admin.music.optionalWithAppleMusic")})</span>}
                      </Label>
                      <div
                        onDragOver={handleAudioDragOver}
                        onDragLeave={handleAudioDragLeave}
                        onDrop={handleAudioDrop}
                        className={`relative border-2 border-dashed rounded-lg p-4 transition-colors ${
                          isDraggingAudio 
                            ? "border-primary bg-primary/5" 
                            : "border-input hover:border-primary/50"
                        }`}
                      >
                        <Input
                          type="file"
                          accept=".mp3,audio/mpeg"
                          onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
                          className="cursor-pointer"
                        />
                        {audioFile && (
                          <p className="text-sm text-muted-foreground mt-2">
                            Selected: {audioFile.name}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                          {isDraggingAudio ? "Drop MP3 file here" : "Drag & drop MP3 file or click to browse"}
                        </p>
                      </div>
                    </div>
                    <div>
                      <Label>{t("admin.music.chordSheet")}</Label>
                      <Input
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={(e) => setChordSheetFile(e.target.files?.[0] || null)}
                      />
                    </div>
                    <div>
                      <Label>{t("admin.music.displayOrder")}</Label>
                      <Input
                        type="number"
                        value={songForm.display_order}
                        onChange={(e) =>
                          setSongForm({ ...songForm, display_order: parseInt(e.target.value) || 0 })
                        }
                      />
                    </div>
                    {isAdmin && (
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={songForm.published}
                          onCheckedChange={(checked) => setSongForm({ ...songForm, published: checked })}
                        />
                        <Label>{t("admin.music.published")}</Label>
                      </div>
                    )}
                    {uploadProgress > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span>{t("admin.music.uploadProgress")}</span>
                          <span>{uploadProgress}%</span>
                        </div>
                        <Progress value={uploadProgress} className="h-2" />
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button onClick={handleSongSubmit} disabled={uploading || translating}>
                      {uploading || translating ? t("admin.music.uploading") : t("admin.music.save")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {songs.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">{t("admin.music.noSongs")}</p>
              ) : (
                songs.map((song) => (
                  <div key={song.id} className="flex items-center justify-between p-3 border rounded">
                    <div className="flex items-center gap-3">
                      <Music className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <div className="flex items-center gap-2">
                          {song.song_number && (
                            <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-1 rounded">
                              #{song.song_number}
                            </span>
                          )}
                          <p className="font-medium">{song.title}</p>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {song.published ? t("admin.music.published") : t("admin.music.unpublished")}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => editSong(song)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteSong(song.id, song.audio_file_path, song.chord_sheet_path)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};